import React from "react";
import styled from "styled-components";
import Markdown from "react-markdown";

/**
 * The article body style, in one place. `BlogArticlePage` and the editor's live
 * preview both render through this, so what a writer sees while drafting is
 * what a reader gets. The backend permalink at `/blog/<slug>` mirrors these
 * rules in plain CSS for the archived copy.
 *
 * `react-markdown` runs without `rehype-raw`, so raw HTML in a post is inert
 * text — matching the server renderer, which escapes before it formats.
 */

export const ArticleBody = styled.div`
  background: white;
  border: 1px solid #ccc;
  padding: 28px 32px;
  box-shadow: 2px 3px rgba(0, 0, 0, 0.1);
  font-size: 1.05rem;
  line-height: 1.7;
  color: #333;
  overflow-wrap: anywhere;

  @media (max-width: 700px) {
    padding: 20px 16px;
    font-size: 1rem;
  }

  h1, h2, h3, h4, h5, h6 {
    color: #222;
    margin-top: 1.5em;
    margin-bottom: 0.5em;
    line-height: 1.3;
  }

  > :first-child {
    margin-top: 0;
  }

  h1 { font-size: 1.6rem; }
  h2 { font-size: 1.35rem; }
  h3 { font-size: 1.15rem; }

  p {
    margin: 0 0 1em;
  }

  a {
    color: #466a5d;
    text-decoration: underline;

    &:hover {
      color: #2a4a3d;
    }
  }

  blockquote {
    border-left: 3px solid #9beece;
    margin: 1em 0;
    padding: 8px 16px;
    color: #555;
    background: #f9f9f9;
  }

  code {
    background: #f5f5f5;
    padding: 2px 6px;
    font-size: 0.9em;
    border: 1px solid #e0e0e0;
    font-family: ui-monospace, SFMono-Regular, Menlo, Consolas, monospace;
  }

  pre {
    background: #f5f5f5;
    padding: 16px;
    overflow-x: auto;
    border: 1px solid #e0e0e0;

    code {
      background: none;
      padding: 0;
      border: none;
    }
  }

  ul, ol {
    padding-left: 1.5em;
    margin: 0 0 1em;
  }

  li {
    margin-bottom: 0.3em;
  }

  img {
    max-width: 100%;
    height: auto;
  }

  hr {
    border: none;
    border-top: 1px solid #ddd;
    margin: 1.5em 0;
  }

  table {
    border-collapse: collapse;
    width: 100%;
    margin: 0 0 1em;
    display: block;
    overflow-x: auto;
  }

  th, td {
    border: 1px solid #ddd;
    padding: 6px 10px;
    text-align: left;
  }
`;

const EmptyPreview = styled.p`
  color: #999;
  font-style: italic;
  margin: 0;
`;

type MarkdownArticleProps = {
  content: string;
  /** shown in place of the body when there is nothing written yet (editor preview) */
  placeholder?: string;
  className?: string;
};

const MarkdownArticle: React.FC<MarkdownArticleProps> = ({
  content,
  placeholder,
  className,
}) => (
  <ArticleBody className={className}>
    {content?.trim() ? (
      <Markdown>{content}</Markdown>
    ) : (
      <EmptyPreview>{placeholder || "nothing to preview yet"}</EmptyPreview>
    )}
  </ArticleBody>
);

export default MarkdownArticle;
